import type { KeyboardEvent } from 'react';

const TABBABLE_SELECTOR = [
  'a[href]:not([disabled])',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"]):not([type="file"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function isHiddenInLayout(el: HTMLElement): boolean {
  if (el.hidden) return true;
  let p: HTMLElement | null = el;
  while (p) {
    const s = window.getComputedStyle(p);
    if (s.display === 'none' || s.visibility === 'hidden') return true;
    p = p.parentElement;
  }
  return false;
}

function listTabbableIn(container: HTMLElement): HTMLElement[] {
  const nodes = Array.from(container.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR));
  return nodes.filter((el) => {
    if (el.hasAttribute('disabled')) return false;
    if (el.tabIndex < 0) return false;
    if (isHiddenInLayout(el)) return false;
    return true;
  });
}

function resolveIndexInTabOrder(tabbable: HTMLElement[], active: HTMLElement | null, container: HTMLElement): number {
  if (!active) return -1;
  let idx = tabbable.indexOf(active);
  if (idx >= 0) return idx;
  let p: HTMLElement | null = active;
  while (p && p !== container) {
    idx = tabbable.indexOf(p);
    if (idx >= 0) return idx;
    p = p.parentElement;
  }
  return -1;
}

/**
 * En un contenedor (p. ej. CardContent del formulario), Enter pasa al siguiente
 * elemento enfocable y Shift+Enter al anterior, en el mismo orden que Tab.
 * No reemplaza a Tab: solo añade atajo de teclado para carga rápida.
 */
export function handleEnterAsTabNavigation(e: KeyboardEvent<HTMLElement>): void {
  if (e.key !== 'Enter') return;
  if (e.defaultPrevented) return;
  if (e.nativeEvent.isComposing) return;
  if (e.ctrlKey || e.altKey || e.metaKey) return;

  const target = e.target as HTMLElement | null;
  if (!target || !e.currentTarget.contains(target)) return;

  if (target.tagName === 'TEXTAREA') return;
  if (target.tagName === 'BUTTON' || target.closest('button')) return;
  if (target.tagName === 'A' && target.hasAttribute('href')) return;
  if (target.isContentEditable) return;

  const container = e.currentTarget;
  const tabbable = listTabbableIn(container);
  if (!tabbable.length) return;

  const active = document.activeElement as HTMLElement | null;
  const idx = resolveIndexInTabOrder(tabbable, active, container);
  if (idx < 0) return;

  const delta = e.shiftKey ? -1 : 1;
  const next = tabbable[idx + delta];
  if (!next) return;

  e.preventDefault();
  next.focus();
}
